import SectionShell from "./SectionShell";
import ToggleSwitch from "../../Setting/components/printers/ToggleSwitch";

const OTP_CHANNEL_OPTIONS = [
  { value: "whatsapp", label: "WhatsApp" },
  { value: "email", label: "Email" },
  { value: "both", label: "Both (customer chooses)" },
];

const FeaturesSection = ({ features, isLoading, isSavingKey, onToggle, emailConfigured }) => {
  const otpUsesEmail = ["email", "both"].includes(features?.otpChannel);

  const rows = [
    { key: "clientLogin", label: "Client Login" },
    {
      key: "loginRequiredToOrder",
      label: "Login Required to Order",
      hint: "Online orders only. Needs Client Login enabled.",
      dependsOn: "clientLogin",
    },
    {
      key: "otpChannel",
      label: "OTP Channel",
      type: "select",
      options: OTP_CHANNEL_OPTIONS,
      dependsOn: "clientLogin",
      hint: !emailConfigured
        ? "Email needs an email account: Restaurant Settings → Email (SMTP). Until then WhatsApp is used."
        : otpUsesEmail
          ? "Email codes go to the email saved on the customer's account."
          : "Where customers receive their login code.",
      warn: !emailConfigured && otpUsesEmail,
    },
    { key: "takeaway", label: "Takeaway" },
    { key: "onlineOrders", label: "Online Orders" },
    { key: "tableBooking", label: "Table Booking" },
    { key: "feedback", label: "Feedbacks" },
    { key: "qrFeature", label: "QR Feature" },
    { key: "availableTimeManage", label: "Menu Availability Scheduler" },
  ];

  const midPoint = Math.ceil(rows.length / 2);
  const columnRows = [rows.slice(0, midPoint), rows.slice(midPoint)];

  const renderTable = (tableRows) => (
    <div className="border border-gray-100 rounded-xl overflow-hidden self-start">
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr className="text-left text-xs text-gray-500 uppercase tracking-widest">
            <th className="px-4 py-3">Feature</th>
            <th className="px-4 py-3">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {tableRows.map((r) => {
            const checked = !!features?.[r.key];
            const isSaving = isSavingKey === r.key;
            // e.g. "Login Required to Order" has no effect while Client Login is off
            const isBlocked = r.dependsOn && !features?.[r.dependsOn];

            return (
              <tr key={r.key} className="bg-white">
                <td className="px-4 py-3">
                  <div className="font-semibold text-gray-900">{r.label}</div>
                  {r.hint && (
                    <div
                      className={`text-xs mt-0.5 ${
                        isBlocked || r.warn ? "text-amber-600" : "text-gray-500"
                      }`}
                    >
                      {r.hint}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className={isSaving || isBlocked ? "opacity-60 pointer-events-none" : ""}>
                    {r.type === "select" ? (
                      <select
                        value={features?.[r.key] || r.options[0].value}
                        onChange={(e) => onToggle(r.key, e.target.value)}
                        className="h-9 px-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-gray-400"
                      >
                        {r.options.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <ToggleSwitch
                        checked={checked}
                        onChange={(next) => onToggle(r.key, next)}
                        labelOn="Enabled"
                        labelOff="Disabled"
                      />
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <SectionShell
      number="4"
      title="Features"
      subtitle="Enable/disable the following features."
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-widest">
          Available Features
        </div>
        {isLoading ? <div className="text-xs text-gray-500">Loading...</div> : null}
      </div>

      <div className="block md:hidden">
        {renderTable(rows)}
      </div>

      <div className="hidden md:grid grid-cols-2 gap-4">
        {columnRows.map((colRows, idx) => {
          if (colRows.length === 0) return null;
          return <div key={idx}>{renderTable(colRows)}</div>;
        })}
      </div>
    </SectionShell>
  );
};

export default FeaturesSection;
